import React, { useRef, useState } from "react";
import { Box, Button, Flex, HStack, IconButton, Text, Textarea, VisuallyHidden } from "@chakra-ui/react";
import { FiFileText, FiPaperclip, FiSend, FiX } from "react-icons/fi";
import { tt } from "../../i18n/tt";
import { ACCEPT_ATTR, MAX_BODY_CHARS, MAX_FILES_PER_MESSAGE, formatBytes, validateFile } from "../../lib/opsChat";

/**
 * 운영팀 대화방 — 입력칸 (첨부 · 끌어다 놓기 · 캡처 붙여넣기)
 *   onSend(body, files) 는 Promise — 성공하면 입력칸을 비운다. 실패하면 그대로 둔다(다시 보내기).
 *   onError(code) — 파일 확인에 걸린 코드("too_big" | "bad_type" | "empty_file" | "too_many_attachments")
 *   enterToSend — PC 는 Enter 보내기 · Shift+Enter 줄바꿈. 모바일은 줄바꿈(보내기 버튼으로 보냄)
 */
export default function OpsChatComposer({ onSend, onError, disabled = false, enterToSend = false, placeholder, variant = "round" }) {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    for (const f of incoming) {
      const bad = validateFile(f);
      if (bad) { onError?.(bad); return; }
    }
    setFiles((prev) => {
      const next = [...prev, ...incoming];
      if (next.length > MAX_FILES_PER_MESSAGE) { onError?.("too_many_attachments"); return prev; }
      return next;
    });
  };

  const canSend = !disabled && !sending && (body.trim() || files.length > 0) && body.length <= MAX_BODY_CHARS;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend(body.trim(), files);
      setBody("");
      setFiles([]);
    } catch {
      // 안내는 onSend 쪽이 띄운다. 입력은 남겨 다시 보낼 수 있게.
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (enterToSend && e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const onPaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (pasted.length) {
      e.preventDefault();
      // 캡처 붙여넣기는 이름이 image.png 하나라 겹치지 않게 시각을 붙인다
      addFiles(pasted.map((f) => (f.name && f.name !== "image.png" ? f
        : new File([f], `캡처_${new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14)}.png`, { type: f.type || "image/png" }))));
    }
  };

  return (
    <Box
      onDragOver={(e) => { if (!disabled) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!disabled) addFiles(e.dataTransfer?.files); }}
      // 끌어다 놓는 동안에만 점선 — outline 을 늘 지정하면 테마가 실선으로 그려 입력칸 둘레에 초록 테두리가 남는다
      sx={dragOver ? { outline: "2px dashed", outlineColor: "brand.400", outlineOffset: "2px" } : undefined}
      borderRadius="8px"
    >
      {files.length > 0 && (
        <Flex gap={2} wrap="wrap" mb={2}>
          {files.map((f, i) => (
            <HStack key={`${f.name}-${i}`} spacing={2} pl={2} pr={1} py={1} border="1px solid" borderColor="neutral.200" borderRadius="8px" bg="neutral.50" fontSize="13px">
              <Box as={FiFileText} color="neutral.500" aria-hidden="true" />
              <Text maxW="180px" noOfLines={1}>{f.name}</Text>
              <Text color="neutral.500">{formatBytes(f.size)}</Text>
              <IconButton size="xs" variant="ghost" icon={<FiX />} aria-label={tt("a.4caea3f2", "첨부 빼기: {{v0}}", { v0: f.name })}
                onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} />
            </HStack>
          ))}
        </Flex>
      )}
      <Flex gap={2} align="flex-end">
        <IconButton
          aria-label={tt("a.cb938be6", "사진·파일 올리기")}
          icon={<FiPaperclip />}
          variant={variant === "round" ? "ghost" : "outline"}
          borderRadius={variant === "round" ? "full" : "8px"}
          minW="44px" h="44px"
          onClick={() => inputRef.current?.click()}
          isDisabled={disabled || sending}
        />
        <VisuallyHidden>
          <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} aria-hidden="true" tabIndex={-1}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </VisuallyHidden>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          aria-label={tt("a.918b33a3", "메시지")}
          placeholder={placeholder || tt("a.1d0e6c46", "메시지 입력")}
          rows={1}
          minH="44px"
          maxH="140px"
          resize="none"
          fontSize="14px"
          bg="neutral.50"
          borderRadius={variant === "round" ? "22px" : "8px"}
          isDisabled={disabled}
        />
        {variant === "round" ? (
          <IconButton aria-label={tt("a.274cb078", "보내기")} icon={<FiSend />} colorScheme="brand" borderRadius="full" minW="44px" h="44px"
            onClick={send} isLoading={sending} isDisabled={!canSend} />
        ) : (
          <Button colorScheme="brand" h="44px" px={5} onClick={send} isLoading={sending} isDisabled={!canSend}>{tt("a.274cb078", "보내기")}</Button>
        )}
      </Flex>
      {body.length > MAX_BODY_CHARS && (
        <Text mt={1} fontSize="12px" color="status.error">{tt("a.587fad48", "메시지는 2,000자까지 보낼 수 있어요.")}</Text>
      )}
    </Box>
  );
}
